import React, { useState, useEffect } from "react";
import { Table, Spin, Tag } from "antd";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import SidebarNav from "../sidebar";
import AdminUserForm from "./AdminUserForm";
import { getAdminUsers, deleteAdminUserApi } from "../../../service/api";
import { usePermission } from "../../../hooks/usePermission";
import { useTableSettings } from "../../../hooks/useTableSettings";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import Select from "react-select";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

const styleDelete = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

const formatDate = (val) => (val ? moment(val).format("DD/MM/YYYY") : "-");

const ALL_COLUMNS = [
  {
    key: "name",
    title: "Name",
    defaultVisible: true,
    render: (r) =>
      `${r?.firstName || ""} ${r?.lastName || ""}`.trim() || "-",
  },
  {
    key: "email",
    title: "Email",
    defaultVisible: true,
    render: (r) => r?.email || "-",
  },
  {
    key: "phoneNumber",
    title: "Phone",
    defaultVisible: true,
    render: (r) => r?.phoneNumber || "-",
  },
  {
    key: "role",
    title: "Role",
    defaultVisible: true,
    render: (r) => (
      <span>
        {r?.roleName || "No Role"}
        {r?.roleType === 1 && (
          <Tag color="gold" style={{ marginLeft: "8px" }}>
            Super Admin
          </Tag>
        )}
      </span>
    ),
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: true,
    render: (r) => (
      <Tag color={r?.isBlock ? "red" : "green"}>
        {r?.isBlock ? "Blocked" : "Active"}
      </Tag>
    ),
  },
  {
    key: "createdAt",
    title: "Created",
    defaultVisible: true,
    render: (r) => formatDate(r?.createdAt),
  },
  {
    key: "updatedAt",
    title: "Updated",
    defaultVisible: false,
    render: (r) => formatDate(r?.updatedAt),
  },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Blocked", value: "blocked" },
];

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "200px",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const AdminUsersPage = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState();
  const { canCreate, canUpdate, canDelete } = usePermission();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "admin-users-list",
    allColumns: ALL_COLUMNS,
  });

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, searchTerm, statusFilter]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        sizePerPage: pageSize,
        pagination: "true",
      };
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (statusFilter?.value === "blocked") {
        params.isBlock = true;
      } else if (statusFilter?.value === "active") {
        params.isBlock = false;
      }
      const res = await getAdminUsers(params);
      if (res?.status && res?.data) {
        setUserList(res.data.result || []);
        setTotal(res.data.total || 0);
      }
    } catch (e) {
      toast.error("Failed to fetch admin users", { toastId: 'admin-users-fetch-error' });
    }
    setLoading(false);
  };

  const handleDownload = async (format) => {
    setDownloadLoading(true);
    try {
      const params = { pagination: false };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter?.value === "blocked") params.isBlock = true;
      else if (statusFilter?.value === "active") params.isBlock = false;
      const res = await getAdminUsers(params);
      const result = res?.data?.result ?? [];
      if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
      const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
      triggerDownload(format, result, cols, "admin-users");
    } catch (e) {
      toast.error("Download failed", { toastId: 'download-failed' });
    }
    setDownloadLoading(false);
  };

  const handleEdit = (record) => {
    setEditId(record._id);
    setShowForm(true);
  };

  const handleDeleteOpen = (record) => {
    setSelectedUser(record);
    setOpenDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      const res = await deleteAdminUserApi(selectedUser._id);
      if (res?.status) {
        toast.success(res?.message || "Admin user deleted successfully", { toastId: 'admin-users-delete-success' });
        setOpenDelete(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(res?.message || "Failed to delete admin user", { toastId: 'admin-users-delete-error' });
      }
    } catch (e) {
      toast.error(e?.data?.message || "Something went wrong", { toastId: 'admin-users-delete-error' });
    }
    setDeleteLoading(false);
  };

  const handleBack = () => {
    setShowForm(false);
    setEditId(null);
    fetchUsers();
  };

  if (showForm) {
    return (
      <>
        <SidebarNav />
        <div className="page-wrapper">
          <AdminUserForm onBack={handleBack} editId={editId} />
        </div>
      </>
    );
  }

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, record) => (
      <div className="text-end">
        {canUpdate("role") && (
          <a
            className="me-1 btn btn-sm bg-success-light"
            onClick={() => handleEdit(record)}
          >
            <i className="fe fe-pencil"></i> Edit
          </a>
        )}
        {canDelete("role") && (
          <a
            className="me-1 btn btn-sm bg-danger-light"
            onClick={() => handleDeleteOpen(record)}
          >
            <i className="fe fe-trash"></i> Delete
          </a>
        )}
      </div>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Admin Users</h3>
              </div>
              <div className="col-auto text-end">
                {canCreate("role") && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditId(null);
                      setShowForm(true);
                    }}
                  >
                    Add Admin User
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card card-table">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                    downloadLoading={downloadLoading}
                  />
                  <Spin spinning={loading}>
                    <Table
                      dataSource={userList}
                      columns={columns}
                      rowKey={(record) => record._id}
                      pagination={false}
                    />
                  </Spin>
                  {total > pageSize && (
                    <CustomPagination
                      key={`pg-${pageSize}`}
                      totalTabs={total}
                      tabsPerPage={pageSize}
                      handleChange={setPage}
                      className={`${
                        loading ? "display-hide" : "display-flex"
                      }`}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={total}
                onClear={() => {
                  setSearchTerm("");
                  setStatusFilter(null);
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Name or email"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Status">
                  <Select
                    options={statusOptions}
                    value={statusFilter || null}
                    onChange={(option) => setStatusFilter(option)}
                    styles={customStyles}
                    placeholder="Any"
                    isClearable
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={openDelete}>
          <Box sx={styleDelete}>
            <h5 style={{ marginBottom: "16px" }}>Delete Admin User</h5>
            <p>
              Are you sure you want to delete{" "}
              <strong>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </strong>
              ?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "24px",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setOpenDelete(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? <Spinner size="sm" /> : "Delete"}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default AdminUsersPage;
